import { getValidAccessToken } from '../lib/auth'
import type {
  Hunt,
  HuntDetail,
  Waypoint,
  WaypointWithClue,
  CheckinResponse,
  ChallengeAnswer,
  ChallengeAnswerResponse,
  ClaimedReward,
  EarnedBadge,
  BadgeWithProgress,
  UserWithStats,
  PaginatedResponse,
  Clue,
  HuntHistoryItem,
} from '../types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

async function getAuthHeaders(): Promise<HeadersInit> {
  const accessToken = await getValidAccessToken()

  return {
    'Content-Type': 'application/json',
    ...(accessToken && {
      Authorization: `Bearer ${accessToken}`,
    }),
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// Hunt API
export async function getHunts(params?: {
  lat?: number
  lon?: number
  radius?: number
  difficulty?: string
  search?: string
  sort?: 'distance' | 'newest' | 'popular'
  page?: number
  per_page?: number
}): Promise<PaginatedResponse<Hunt>> {
  const searchParams = new URLSearchParams()
  if (params?.lat) searchParams.set('lat', params.lat.toString())
  if (params?.lon) searchParams.set('lon', params.lon.toString())
  if (params?.radius) searchParams.set('radius', params.radius.toString())
  if (params?.difficulty) searchParams.set('difficulty', params.difficulty)
  if (params?.search) searchParams.set('search', params.search)
  if (params?.sort) searchParams.set('sort', params.sort)
  if (params?.page) searchParams.set('page', params.page.toString())
  if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

  const query = searchParams.toString()
  return fetchApi<PaginatedResponse<Hunt>>(`/hunts${query ? `?${query}` : ''}`)
}

export async function getHunt(huntId: string): Promise<HuntDetail> {
  return fetchApi<HuntDetail>(`/hunts/${huntId}`)
}

export async function startHunt(huntId: string): Promise<{
  progress_id: string
  hunt_id: string
  status: string
  current_waypoint_index: number
  started_at: string
  first_clue?: Clue
}> {
  return fetchApi(`/hunts/${huntId}/start`, { method: 'POST' })
}

// Waypoint API
export async function getWaypoints(huntId: string): Promise<{
  waypoints: (Waypoint | WaypointWithClue)[]
  total_waypoints: number
  completed_count: number
  current_index: number
}> {
  return fetchApi(`/hunts/${huntId}/waypoints`)
}

export async function checkinWaypoint(
  waypointId: string,
  location: { latitude: number; longitude: number; accuracy?: number }
): Promise<CheckinResponse> {
  return fetchApi(`/waypoints/${waypointId}/checkin`, {
    method: 'POST',
    body: JSON.stringify(location),
  })
}

export async function answerChallenge(
  waypointId: string,
  answer: ChallengeAnswer
): Promise<ChallengeAnswerResponse> {
  return fetchApi(`/waypoints/${waypointId}/challenge`, {
    method: 'POST',
    body: JSON.stringify(answer),
  })
}

// User API
export async function getCurrentUser(): Promise<UserWithStats> {
  return fetchApi<UserWithStats>('/users/me')
}

export async function getMyRewards(): Promise<{
  rewards: ClaimedReward[]
  total: number
}> {
  return fetchApi('/rewards/my')
}

export async function getMyBadges(): Promise<{
  badges: EarnedBadge[]
  total: number
}> {
  return fetchApi('/badges/my')
}

export async function getAllBadges(): Promise<{
  badges: BadgeWithProgress[]
  total: number
}> {
  return fetchApi('/badges')
}

export async function getMyHunts(): Promise<{
  items: HuntHistoryItem[]
  total: number
}> {
  return fetchApi('/users/me/hunts')
}

// Reports API
export async function submitReport(
  huntId: string,
  data: { category: string; waypoint_id?: string; description?: string }
): Promise<{ id: string; message: string }> {
  return fetchApi(`/hunts/${huntId}/report`, { method: 'POST', body: JSON.stringify(data) })
}

export async function getAdminReports(params?: {
  status?: string
  category?: string
  page?: number
}): Promise<{
  reports: AdminReport[]
  total: number
  page: number
  page_size: number
}> {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.category) searchParams.set('category', params.category)
  if (params?.page) searchParams.set('page', params.page.toString())
  const query = searchParams.toString()
  return fetchApi(`/admin/reports${query ? `?${query}` : ''}`)
}

export async function updateReportStatus(
  reportId: string,
  data: { status: string; admin_note?: string }
): Promise<AdminReport> {
  return fetchApi(`/admin/reports/${reportId}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export interface AdminReport {
  id: string
  hunt_id: string
  hunt_title: string
  waypoint_id: string | null
  waypoint_place_name: string | null
  user_id: string
  user_display_name: string
  category: string
  description: string | null
  status: string
  admin_note: string | null
  created_at: string
  updated_at: string
}
